import { useCallback, useEffect, useRef, useState } from 'react'
import { TerraDraw, TerraDrawPolygonMode } from 'terra-draw'
import { TerraDrawGoogleMapsAdapter } from 'terra-draw-google-maps-adapter'

type Polygon = { type: 'Polygon'; coordinates: number[][][] }

const POLYGON_STYLE = {
  fillColor: '#487CCA',
  fillOpacity: 0.2,
  outlineColor: '#487CCA',
  outlineWidth: 2,
} as const

/** "lng,lat" per corner, longitude first as PostGIS expects. */
export const polygonToVertices = (polygon: Polygon): string[] =>
  polygon.coordinates[0].slice(0, -1).map(([lng, lat]) => `${lng},${lat}`)

export const verticesToPath = (
  vertices: string[]
): google.maps.LatLngLiteral[] =>
  vertices.map(v => {
    const [lng, lat] = v.split(',').map(Number)
    return { lat, lng }
  })

/**
 * Draws one new polygon on a Google map. Finishing it frames the polygon,
 * reports its corners and leaves drawing mode. The finished polygon belongs to
 * the caller; `isSketching` says when to hide it because a new one is under way.
 */
export function usePolygonDraw(
  map: google.maps.Map | null,
  onComplete: (vertices: string[]) => void,
  getFitPadding?: (mapDiv: HTMLElement) => google.maps.Padding
) {
  const [isMapReady, setIsMapReady] = useState(false)
  const [isDrawing, setIsDrawing] = useState(false)
  const [isSketching, setIsSketching] = useState(false)
  const drawRef = useRef<TerraDraw | null>(null)
  const onCompleteRef = useRef(onComplete)
  onCompleteRef.current = onComplete
  const getFitPaddingRef = useRef(getFitPadding)
  getFitPaddingRef.current = getFitPadding

  // The Google adapter attaches to an inner map element that exists only
  // after the map has rendered once; starting earlier throws.
  useEffect(() => {
    setIsMapReady(false)
    if (!map) return
    const listener = google.maps.event.addListenerOnce(map, 'idle', () =>
      setIsMapReady(true)
    )
    return () => google.maps.event.removeListener(listener)
  }, [map])

  const cancel = useCallback(() => {
    drawRef.current?.stop()
    drawRef.current = null
    setIsDrawing(false)
    setIsSketching(false)
  }, [])

  const start = useCallback(() => {
    if (!map || !isMapReady || drawRef.current) return

    const draw = new TerraDraw({
      adapter: new TerraDrawGoogleMapsAdapter({
        lib: google.maps,
        map,
        coordinatePrecision: 9,
      }),
      modes: [new TerraDrawPolygonMode({ styles: POLYGON_STYLE })],
    })
    draw.on('ready', () => draw.setMode('polygon'))
    draw.on('change', (_ids, type) => {
      if (type === 'create') setIsSketching(true)
    })
    draw.on('finish', (id, { action }) => {
      if (action !== 'draw') return
      const feature = draw.getSnapshotFeature(id)
      if (feature?.geometry.type !== 'Polygon') return

      const vertices = polygonToVertices(feature.geometry)
      const bounds = new google.maps.LatLngBounds()
      verticesToPath(vertices).forEach(p => bounds.extend(p))
      map.fitBounds(bounds, getFitPaddingRef.current?.(map.getDiv()))

      onCompleteRef.current(vertices)
      // Stop once Terra Draw has finished handling this click.
      setTimeout(cancel)
    })
    draw.start()
    drawRef.current = draw
    setIsDrawing(true)
  }, [map, isMapReady, cancel])

  useEffect(() => cancel, [map, cancel])

  return { isDrawing, isSketching, start, cancel }
}
